import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Image as ImageIcon, Presentation, Target, Clock, Lock, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Student Labs catalog page.
 * Hits GET /api/labs/catalog and renders Lab 8 / 9 / 10 cards for SakethStudio.
 * Non-whitelisted users still see the cards but get a lock banner.
 */
export default function Labs() {
  const [state, setState] = useState({ loading: true, whitelisted: false, labs: [] });

  useEffect(() => {
    fetch(`${API}/api/labs/catalog`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setState({ loading: false, whitelisted: !!d.whitelisted, labs: d.labs || [] }))
      .catch(() => setState({ loading: false, whitelisted: false, labs: [] }));
  }, []);

  const iconFor = (id) => ({ 8: ImageIcon, 9: Presentation, 10: Target }[id] || FlaskConical);
  const colorFor = (id) => ({ 8: '#f59e0b', 9: '#6366f1', 10: '#ec4899' }[id] || '#8b5cf6');

  if (state.loading) return <div style={{ padding: 32, color: '#9ca3af' }}>Loading labs…</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }} data-testid="labs-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 24, background: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FlaskConical size={22} color="#8b5cf6" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600 }}>Student Labs</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>Guided AI exercises from the FoundryAI Lab Manual — Studio platform.</p>
        </div>
      </div>

      {!state.whitelisted && (
        <div data-testid="labs-access-banner" style={{ marginBottom: 20, padding: '12px 16px', background: '#fef3c720', border: '1px solid #fbbf24', borderRadius: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Lock size={16} color="#d97706" style={{ marginTop: 2 }} />
          <div style={{ fontSize: 14 }}>
            <strong>You can browse these labs, but running one needs instructor approval.</strong>
            <div style={{ marginTop: 2, color: '#6b7280', fontSize: 12 }}>
              Ask your instructor (or the FoundryAI admin) to add your email to the labs whitelist.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
        {state.labs.map(lab => {
          const Icon = iconFor(lab.id);
          const color = colorFor(lab.id);
          return (
            <div key={lab.id} data-testid={`lab-card-${lab.id}`} style={{ padding: 20, border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} color={color} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: '#059669', background: '#d1fae5', padding: '2px 8px', borderRadius: 10 }}>AVAILABLE</span>
              </div>
              <h3 style={{ margin: '4px 0 2px', fontSize: 16, fontWeight: 600 }}>Lab {lab.id}: {lab.title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9ca3af', fontSize: 12, marginBottom: 10 }}>
                <Clock size={11} /> {lab.duration_min} min · steps {lab.manual_steps}
              </div>
              <p style={{ margin: 0, color: '#4b5563', fontSize: 13, lineHeight: 1.5, flex: 1 }}>{lab.summary}</p>
              <Link
                to={lab.route}
                data-testid={`lab-start-btn-${lab.id}`}
                style={{
                  marginTop: 14, padding: '8px 14px', borderRadius: 8,
                  background: state.whitelisted ? '#8b5cf6' : 'transparent',
                  color: state.whitelisted ? '#fff' : '#8b5cf6',
                  border: state.whitelisted ? 'none' : '1px solid #8b5cf6',
                  textDecoration: 'none', textAlign: 'center', fontSize: 13, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                {state.whitelisted ? 'Start Lab' : 'Preview (locked)'} <ArrowRight size={13} />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}
